import type { CollectionRole, Member, Root, TaskRole } from "../domain/app-types.ts";
import type { Todo } from "../domain/entities.ts";
import type { StoredEntity } from "../infrastructure/storage/indexed-db.ts";
import {
  canCommentInAccessState,
  canEditInAccessState,
  canEditTaskInRoot,
  type AccessPolicyState,
} from "../features/collaboration/access-policy.ts";

export interface WorkspaceSessionSource {
  roots: Root[];
  activeRootId: string | null;
  activeCollectionId: string | null;
}

export interface WorkspaceDataSource {
  todos: Todo[];
  categoryName(id: string): string;
}

export interface WorkspaceAccessSource {
  taskRoles: ReadonlyMap<string, TaskRole>;
  collectionRoles: ReadonlyMap<string, CollectionRole>;
  scopeKey(roots: Root[]): string;
  entityVisible(entity: StoredEntity): boolean;
  members(rootId: string | null): Member[];
  taskMembers(rootId: string | null, collectionId: string | null): Member[];
  memberEmail(id: string): string;
}

export function createWorkspaceContext(session: WorkspaceSessionSource, data: WorkspaceDataSource, access: WorkspaceAccessSource) {
  const policyState = (): AccessPolicyState => ({
    roots: session.roots,
    activeRootId: session.activeRootId,
    activeCollectionId: session.activeCollectionId,
    tasks: data.todos,
    taskRoles: access.taskRoles,
    collectionRoles: access.collectionRoles,
  });

  return {
    activeRootName: (): string => session.roots.find(root => root.id === session.activeRootId)?.name || "No space",
    spaceName: (rootId: string): string => session.roots.find(root => root.id === rootId)?.name || "Shared task",
    activeRootRole: (): string => session.roots.find(root => root.id === session.activeRootId)?.role || "viewer",
    canEdit: (taskId?: string): boolean => canEditInAccessState(policyState(), taskId),
    canEditTaskInRoot: (task: Todo, rootId: string): boolean => canEditTaskInRoot(policyState(), task, rootId),
    canComment: (taskId: string): boolean => canCommentInAccessState(policyState(), taskId),
    accessScopeKey: (): string => access.scopeKey(session.roots),
    entityVisible: (entity: StoredEntity): boolean => access.entityVisible(entity),
    activeDirectory: (): Member[] => access.members(session.activeRootId),
    taskDirectory: (collectionId: string | null): Member[] => access.taskMembers(session.activeRootId, collectionId),
    memberEmail: (id: string): string => access.memberEmail(id),
    categoryName: (id: string): string => data.categoryName(id),
  };
}
